import { z } from 'zod'

import {
  type GradeElementaryMiddle,
  type GradeHigh,
  type NumberGradeElementary,
  type Revision,
  SCHOOL_TYPE_KO_ABBR,
  SCHOOL_TYPE_KO_FULL,
  type SchoolType,
  type Semester,
} from '~/@entities/SchoolSystem'
import type {
  CommonResponseChaptersResponse,
  CommonResponseCollectionCurriculumResponse,
  CommonResponseCollectionCurriculumTreeNodeResponse,
  CommonResponseCoreCurriculumDto,
  CommonResponseListPublisherAndCurriculumResponse,
  CommonResponseProblemCurriculumDifferenceResponse,
  CurriculumBySchoolTestResponse,
  CurriculumTreeResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'

import {
  HIGH_SCHOOL_CATEGORIZED_GRADE,
  NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS,
  PRE_OPEN_CURRICULUM_KEYS,
} from './constants'
import { formatCurriculumCoreLabels } from './formats'
import {
  type Curriculum,
  type CurriculumChapterSearch,
  CurriculumChapterSearchSchema,
  type CurriculumChapterTree,
  CurriculumChapterTreeSchema,
  type CurriculumConceptChapterTree,
  CurriculumConceptChapterTreeSchema,
  type CurriculumConceptTreeNode,
  CurriculumConceptTreeNodeSchema,
  type CurriculumCore,
  type CurriculumCoreItem,
  type CurriculumCoreNodes,
  CurriculumCoreNodesSchema,
  type CurriculumCoreSchool,
  type CurriculumOpenStatus,
  CurriculumSchema,
  type CurriculumStatus,
  type PublisherCurriculum,
  type PublisherCurriculumDifference,
  PublisherCurriculumDifferenceSchema,
  PublisherCurriculumSchema,
  type TrieKey,
} from './schemas'

// 스펙이 nullable 을 optional 로만 그린다. CurriculumResponse 의 url·revisionId~littleChapterName 16필드와
// 트리 응답(CurriculumTreeResponse)의 semester 는 서버가 `?` 로 선언하고
// JsonInclude 없이 null 을 직렬화한다. 2026-10-07 백엔드 develop 의 curriculum/controller/response 로 확인.
// 백엔드가 nullable 을 스펙에 싣으면 이 캐스트와 주석만 지운다.
const asWire = (
  raw:
    | CommonResponseCollectionCurriculumResponse['data']
    | CurriculumTreeResponse[]
    | CurriculumBySchoolTestResponse[],
): unknown => raw

export const adaptCurriculums = (
  raw: CommonResponseCollectionCurriculumResponse['data'],
): Curriculum[] => parseApiResponse(z.array(CurriculumSchema), asWire(raw), 'Curriculum')

export const adaptCurriculumCoreNodes = (
  raw: CommonResponseCoreCurriculumDto['data'],
): CurriculumCoreNodes => parseApiResponse(CurriculumCoreNodesSchema, raw, 'CurriculumCore')

export const adaptCurriculumConceptChapterTrees = (
  raw: CurriculumTreeResponse[],
): CurriculumConceptChapterTree[] =>
  parseApiResponse(
    z.array(CurriculumConceptChapterTreeSchema),
    asWire(raw),
    'CurriculumChapterTree',
  )

export const adaptCurriculumChapterTrees = (
  raw: CurriculumBySchoolTestResponse[],
): CurriculumChapterTree[] =>
  parseApiResponse(z.array(CurriculumChapterTreeSchema), asWire(raw), 'CurriculumChapterTree')

export const adaptCurriculumChapterSearch = (
  raw: CommonResponseChaptersResponse['data'],
): CurriculumChapterSearch =>
  parseApiResponse(CurriculumChapterSearchSchema, raw, 'CurriculumChapterSearch')

// 트리 노드의 url 과 유형(conceptList)의 url 은 서버가 `String?` 로 선언하고 키를 빼지 않고 null 을 싣는데 스펙은
// optional 로만 그린다. 유형 항목은 생성 타입이 이름이 같은 다른 Concept 클래스로 덮어썼다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 curriculum/controller/response/CurriculumTreeNodeResponse.kt 로 확인.
const asWireTreeNodes = (
  raw: CommonResponseCollectionCurriculumTreeNodeResponse['data'],
): unknown => raw

export const adaptCurriculumConceptTreeNodes = (
  raw: CommonResponseCollectionCurriculumTreeNodeResponse['data'],
): CurriculumConceptTreeNode[] =>
  parseApiResponse(z.array(CurriculumConceptTreeNodeSchema), asWireTreeNodes(raw), 'Curriculum')

// 출판사 교과서 학기 목록의 항목(PublisherAndCurriculumResponse.Curriculum)은 생성 타입이 이름이 같은 교육과정
// Curriculum 클래스로 덮어썼고, 단원 차이의 description 은 서버가 `String?` 인데 스펙이 optional 로만 그린다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 curriculum/controller/response 두 파일로 확인.
const asWirePublisher = (
  raw:
    | CommonResponseListPublisherAndCurriculumResponse['data']
    | CommonResponseProblemCurriculumDifferenceResponse['data'],
): unknown => raw

export const adaptPublisherCurriculums = (
  raw: CommonResponseListPublisherAndCurriculumResponse['data'],
): PublisherCurriculum[] =>
  parseApiResponse(z.array(PublisherCurriculumSchema), asWirePublisher(raw), 'Curriculum')

export const adaptPublisherCurriculumDifference = (
  raw: CommonResponseProblemCurriculumDifferenceResponse['data'],
): PublisherCurriculumDifference =>
  parseApiResponse(PublisherCurriculumDifferenceSchema, asWirePublisher(raw), 'Curriculum')

const toOpenStatus = (trieKey: TrieKey, status: CurriculumStatus): CurriculumOpenStatus => {
  if ((Object.values(NON_STANDARD_HIGH_SCHOOL_CURRICULUM_KEYS) as TrieKey[]).includes(trieKey))
    return 'HIDDEN'
  if (status !== 'ACTIVE') return 'HIDDEN'
  return PRE_OPEN_CURRICULUM_KEYS.includes(trieKey) ? 'PRE_OPEN' : 'ACTIVE'
}

const toCoreItem = <T extends SchoolType>(node: {
  id: Curriculum['id']
  trieKey: TrieKey
  revision: Revision
  status: CurriculumStatus
  schoolType: T
  grade: NumberGradeElementary | null
  subject: GradeHigh | null
  semester: Semester
  orderingNumber?: number
}): CurriculumCoreItem<T> => {
  const gradeSubject = node.subject ?? (`${node.grade}` as GradeElementaryMiddle)
  const categorizedGrade =
    node.grade !== null
      ? (`${node.grade}` as GradeElementaryMiddle)
      : HIGH_SCHOOL_CATEGORIZED_GRADE[node.trieKey] ?? '3'

  return {
    isAllType: false,
    id: node.id,
    trieKey: node.trieKey,
    upperTrieKey: node.trieKey.split('.').slice(0, -1).join('.'),
    lowerTrieKeys: node.schoolType === 'HIGH' || node.semester ? [node.trieKey] : [],
    revision: node.revision,
    schoolType: node.schoolType,
    gradeSubject,
    semester: node.semester,
    orderingNumber: node.orderingNumber,
    status: toOpenStatus(node.trieKey, node.status),
    ...formatCurriculumCoreLabels({ ...node, gradeSubject }),
    categorizedGrade,
  }
}

const toCoreSchools = (schoolTypes: CurriculumCoreNodes['schoolTypes']): CurriculumCoreSchool[] =>
  [...new Set(schoolTypes.map(({ schoolType }) => schoolType))].map((schoolType) => {
    const nodes = schoolTypes.filter((node) => node.schoolType === schoolType)
    return {
      value: schoolType,
      label: SCHOOL_TYPE_KO_ABBR[schoolType],
      labelFull: SCHOOL_TYPE_KO_FULL[schoolType],
      revisions: nodes.map(({ revision }) => revision),
      trieKeys: nodes.map(({ trieKey }) => trieKey),
      status: 'ACTIVE',
    }
  })

/** 학년·학기·과목 축을 화면이 쓰는 항목으로 조립한다. 학년의 하위 키에는 그 아래 학기들을 모은다 */
export const toCurriculumCore = (nodes: CurriculumCoreNodes): CurriculumCore => {
  const gradeItems = [...nodes.grades]
    .sort((a, b) => {
      if (a.grade !== b.grade) return a.grade - b.grade
      if (a.revision === 'CURRICULUM_22' && b.revision !== 'CURRICULUM_22') return -1
      if (a.revision !== 'CURRICULUM_22' && b.revision === 'CURRICULUM_22') return 1
      return 0
    })
    .map((node) => toCoreItem({ ...node, subject: null, semester: null }))

  const semesters = [...nodes.semesters]
    .sort((a, b) => a.orderingNumber - b.orderingNumber)
    .map((node) => toCoreItem({ ...node, subject: null }))

  const gradeOf = (semester: (typeof semesters)[number]) =>
    gradeItems.find(
      (grade) =>
        grade.gradeSubject === semester.gradeSubject &&
        grade.revision === semester.revision &&
        grade.schoolType === semester.schoolType,
    )
  const grades = gradeItems.map((grade) => ({
    ...grade,
    lowerTrieKeys: [
      ...new Set([
        ...grade.lowerTrieKeys,
        ...semesters
          .filter((semester) => gradeOf(semester) === grade)
          .map(({ trieKey }) => trieKey),
      ]),
    ],
  }))

  const subjects = [...nodes.subjects]
    .sort((a, b) => a.orderingNumber - b.orderingNumber)
    .map((node) => toCoreItem({ ...node, grade: null, semester: null }))

  return {
    revisions: nodes.revisions,
    schoolTypes: nodes.schoolTypes,
    schools: toCoreSchools(nodes.schoolTypes),
    grades,
    semesters,
    subjects,
  }
}
